import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { PageHeader } from "@/components/PageHeader";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { FileText, RefreshCw, Search, Loader2, Eye } from "lucide-react";

const STATUS_STYLE = {
  approved: "border-emerald-500/40 bg-emerald-500/10 text-emerald-600",
  pending: "border-amber-500/40 bg-amber-500/10 text-amber-600",
  rejected: "border-red-500/40 bg-red-500/10 text-red-600",
};

export default function Templates() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const canSync = (user?.permissions || []).includes("whatsapp.templates.sync");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [detail, setDetail] = useState(null);
  const [syncing, setSyncing] = useState(false);

  const { data, isLoading } = useQuery({ queryKey: ["wa-templates"], queryFn: async () => (await api.get("/whatsapp/templates")).data.data });
  let templates = data?.templates || [];
  if (search) templates = templates.filter((t) => t.name.toLowerCase().includes(search.toLowerCase()));
  if (statusFilter !== "all") templates = templates.filter((t) => t.status === statusFilter);

  const sync = async () => {
    setSyncing(true);
    try { const { data } = await api.post("/whatsapp/templates/sync"); toast.success(`Sincronizzati ${data.data.count} template`); qc.invalidateQueries({ queryKey: ["wa-templates"] }); }
    catch (e) { toast.error(apiError(e)); }
    finally { setSyncing(false); }
  };

  return (
    <div>
      <PageHeader title="Template" subtitle="Modelli di messaggio WhatsApp sincronizzati da Meta" icon={FileText}
        actions={canSync && <Button className="rounded-lg" onClick={sync} disabled={syncing} data-testid="sync-templates-btn">{syncing ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} Sincronizza da Meta</Button>} />

      <div className="mb-4 flex flex-col gap-3 sm:flex-row">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cerca template..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-9" data-testid="tpl-search" />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-full sm:w-48" data-testid="tpl-status-filter"><SelectValue /></SelectTrigger>
          <SelectContent><SelectItem value="all">Tutti gli stati</SelectItem><SelectItem value="approved">Approvati</SelectItem><SelectItem value="pending">In attesa</SelectItem><SelectItem value="rejected">Rifiutati</SelectItem></SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">{Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-40 rounded-xl" />)}</div>
      ) : templates.length === 0 ? (
        <Card className="border-dashed"><CardContent className="flex flex-col items-center gap-2 py-12 text-center"><FileText className="h-10 w-10 text-muted-foreground" /><p className="text-sm text-muted-foreground">Nessun template. Sincronizza dai canali WhatsApp.</p></CardContent></Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {templates.map((t) => (
            <Card key={t.id} data-testid={`tpl-${t.name}`}>
              <CardContent className="p-5">
                <div className="mb-2 flex items-start justify-between">
                  <div><p className="font-medium">{t.name}</p><p className="text-xs text-muted-foreground">{t.language} · {t.category}</p></div>
                  <Badge variant="outline" className={STATUS_STYLE[t.status] || "border-slate-400/40 bg-slate-400/10 text-slate-500"}>{t.status}</Badge>
                </div>
                <p className="line-clamp-2 text-sm text-muted-foreground">{bodyText(t)}</p>
                <div className="mt-3 flex items-center justify-between">
                  {!canSend(t) && <span className="text-xs text-amber-600">Non inviabile</span>}
                  {canSend(t) && <span className="text-xs text-emerald-600">Utilizzabile</span>}
                  <Button variant="ghost" size="sm" onClick={() => setDetail(t)} data-testid={`tpl-view-${t.name}`}><Eye className="mr-1 h-3.5 w-3.5" /> Dettaglio</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent data-testid="tpl-detail">
          <DialogHeader><DialogTitle>{detail?.name}</DialogTitle></DialogHeader>
          {detail && (
            <div className="space-y-3 text-sm">
              <div className="flex gap-2"><Badge variant="secondary">{detail.language}</Badge><Badge variant="secondary">{detail.category}</Badge><Badge variant="outline" className={STATUS_STYLE[detail.status]}>{detail.status}</Badge></div>
              <div className="space-y-2">
                {(detail.components || []).map((c, i) => (
                  <div key={i} className="rounded-lg border border-border p-3">
                    <p className="text-xs font-medium text-muted-foreground">{c.type}</p>
                    <p className="mt-1">{c.text || (c.buttons ? c.buttons.map((b) => b.text).join(", ") : "—")}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function bodyText(t) {
  const b = (t.components || []).find((c) => (c.type || "").toUpperCase() === "BODY");
  return b?.text || "—";
}
function canSend(t) { return t.status === "approved"; }
